import { Head, Link } from '@inertiajs/react';
import PublicLayout from '@/layouts/public-layout';
import { User } from 'lucide-react';
import { useTranslatedRoutes } from '@/hooks/use-translated-routes';
import { useTranslations } from '@/hooks/use-translations';
import { SeoHead } from '@/components/seo-head';

interface Author {
    id: string;
    name: string;
    slug: string;
    bio: string | null;
    books_count: number;
}

interface AuthorsPageProps {
    authors: Author[];
    locale: string;
}

export default function Authors({ authors, locale }: AuthorsPageProps) {
    const { authorUrl } = useTranslatedRoutes();
    const { t } = useTranslations();


    // Get the base URL from environment
    const baseUrl = import.meta.env.VITE_APP_URL || 'http://localhost:8000';
    
    // Generate structured data for authors collection
    const authorsJsonLd = {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": t('Authors'),
        "description": t('Browse our collection of authors and discover their works'),
        "numberOfItems": authors.length,
        "hasPart": authors.map(author => ({
            "@type": "Person",
            "name": author.name,
            "url": `${baseUrl}${authorUrl(author.slug)}`,
            "description": author.bio
        }))
    };

    return (
        <PublicLayout>
            <SeoHead
                title={t('Authors')}
                description={t('Discover the brilliant minds behind our book collection. Browse all authors and their works.')}
                jsonLd={authorsJsonLd}
            />

            {/* Header Section */}
            <section className="py-6 md:py-8">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-8">
                        <h1 className="text-2xl md:text-3xl font-bold mb-2">
                            {t('Browse Authors')}
                        </h1>
                        <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-8">
                            {t('Discover the brilliant minds behind our book collection')}
                        </p>
                        <div className="max-w-4xl mx-auto">
                            <hr className="border-t border-border" />
                        </div>
                    </div>

                    {/* Authors List */}
                    {authors && authors.length > 0 ? (
                        <div className="max-w-4xl mx-auto">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 sm:gap-x-8 gap-y-1">
                                {authors.map((author) => (
                                    <Link
                                        key={author.id}
                                        href={authorUrl(author.slug)}
                                        className="group flex items-center justify-between py-2 px-3 rounded-md hover:bg-muted/50 transition-colors"
                                    >
                                        <span className="text-sm font-medium group-hover:text-primary transition-colors">
                                            {author.name}
                                        </span>
                                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                                            {author.books_count}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-16">
                            <User className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
                            <h2 className="text-2xl font-semibold mb-2">{t('No authors found')}</h2>
                            <p className="text-muted-foreground">
                                {t("Author profiles haven't been added yet. Check back later!")}
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}